import type { ShopperAppearance } from "~/server/shopper";

/**
 * The chat wears the retailer's colours, not Hephon's. Components use these variables
 * (`bg-(--shop-brand)`, `text-(--shop-deal)`…); the admin app keeps its own tokens untouched.
 *
 * The theme is a stylesheet on the chat's root, not an inline style: "auto" needs a media
 * query. On the hosted page that root is the document (`PAGE_SCOPE`): the page is the chat, and
 * its toasts render in <body>. A chat mounted inside one of our own pages (the Configure
 * preview, the public site's demo store) scopes it to its own element, so the page around it
 * keeps its tokens; its drawer and dialogs portal into that element (`ChatFrame` in chrome.tsx).
 * Every value written here is a schema-checked hex colour or one of ours, so nothing free-form
 * reaches CSS.
 *
 * A colour does three different jobs, and each has its own variable because a dark scheme
 * pulls them apart: a fill (`--shop-brand`, text on it is `--shop-on-brand`), text on the page
 * (`--shop-brand-text`: a navy brand is unreadable on a dark surface), and the raw deep colour
 * the live voice screen sits on (`--shop-deep`).
 */

type Rgb = readonly [number, number, number];

const WHITE: Rgb = [255, 255, 255];
/** Text on a fill too light for white. */
const INK: Rgb = [20, 22, 26];

function rgb(hex: string): Rgb {
	const value = Number.parseInt(hex.slice(1), 16);
	return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

function hex(color: Rgb): string {
	return `#${color.map((c) => Math.round(c).toString(16).padStart(2, "0")).join("")}`;
}

/** `share` of `b` in `a`, in sRGB: close enough for picking a readable shade. */
function mix(a: Rgb, b: Rgb, share: number): Rgb {
	return [0, 1, 2].map(
		(i) => (a[i] as number) * (1 - share) + (b[i] as number) * share,
	) as unknown as Rgb;
}

function luminance(color: Rgb): number {
	const [r, g, b] = color.map((c) => {
		const v = c / 255;
		return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
	}) as [number, number, number];
	return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio, 1 to 21. */
export function contrast(a: Rgb, b: Rgb): number {
	const [light, dark] = [luminance(a), luminance(b)].sort(
		(x, y) => y - x,
	) as [number, number];
	return (light + 0.05) / (dark + 0.05);
}

/** `color`, moved toward `toward` only as far as it takes to read at `min` contrast on `on`. */
function readable(color: Rgb, on: Rgb, min: number, toward: Rgb): Rgb {
	for (let share = 0; share < 1; share += 0.08) {
		const shade = mix(color, toward, share);
		if (contrast(shade, on) >= min) {
			return shade;
		}
	}
	return toward;
}

/** White on a fill, unless the fill is too light to carry it. */
const onFill = (fill: Rgb): Rgb => (contrast(WHITE, fill) >= 2.2 ? WHITE : INK);

type Vars = Record<string, string>;

/**
 * The parts that can have a colour of their own: the header, the shopper's bubble, the view
 * basket and checkout buttons. Each falls back to the brand's, and text on a chosen colour is
 * picked for contrast, so no choice makes a part unreadable.
 */
function parts(
	appearance: ShopperAppearance,
	brand: string,
	onBrand: string,
	cta: string,
): Vars {
	const { headerColor, bubbleColor, ctaColor } = appearance;
	const on = (fill: string) => hex(onFill(rgb(fill)));
	const filledHeader = appearance.header === "brand" || Boolean(headerColor);
	const header = headerColor ?? brand;
	const onHeader = headerColor ? on(headerColor) : onBrand;
	const softBubble = appearance.bubble === "soft" && !bubbleColor;
	return {
		"--shop-header": filledHeader ? header : "var(--shop-surface)",
		"--shop-on-header": filledHeader ? onHeader : "var(--shop-ink)",
		// The basket button must not vanish into a header of its own colour.
		"--shop-header-action": filledHeader ? onHeader : brand,
		"--shop-on-header-action": filledHeader ? header : onBrand,
		"--shop-bubble":
			bubbleColor ?? (softBubble ? "var(--shop-brand-soft)" : brand),
		"--shop-on-bubble": bubbleColor
			? on(bubbleColor)
			: softBubble
				? "var(--shop-ink)"
				: onBrand,
		"--shop-cta": ctaColor ?? cta,
		"--shop-on-cta": on(ctaColor ?? cta),
		"--shop-checkout": ctaColor ?? brand,
		"--shop-on-checkout": ctaColor ? on(ctaColor) : onBrand,
	};
}

/**
 * Text and line colours the retailer or its app chose, over the derived ones. Like every
 * colour here, text is moved only as far as it takes to read on the card it sits on (4.5 for
 * text, 3 for small print), so no choice makes the chat unreadable. A line is decoration and
 * is used as given.
 */
function chosen(appearance: ShopperAppearance, card: Rgb): Vars {
	const { textColor, mutedColor, lineColor } = appearance;
	const toward = luminance(card) < 0.2 ? WHITE : INK;
	return {
		...(textColor
			? { "--shop-ink": hex(readable(rgb(textColor), card, 4.5, toward)) }
			: {}),
		...(mutedColor
			? {
					"--shop-muted": hex(
						readable(rgb(mutedColor), card, 3, toward),
					),
				}
			: {}),
		...(lineColor ? { "--shop-brand-line": lineColor } : {}),
	};
}

function lightVars(appearance: ShopperAppearance): Vars {
	const { primaryColor, deepColor, accentColor, dealColor } = appearance;
	const onBrand = hex(onFill(rgb(primaryColor)));
	const card = appearance.cardColor;
	return {
		"color-scheme": "light",
		"--shop-brand": primaryColor,
		"--shop-on-brand": onBrand,
		"--shop-brand-text": hex(readable(rgb(primaryColor), WHITE, 3, INK)),
		"--shop-deep": deepColor,
		"--shop-accent": accentColor,
		"--shop-deal": dealColor,
		"--shop-brand-soft": `color-mix(in oklab, ${primaryColor} 8%, white)`,
		"--shop-brand-line": `color-mix(in oklab, ${primaryColor} 16%, white)`,
		"--shop-accent-soft": `color-mix(in oklab, ${accentColor} 35%, white)`,
		"--shop-accent-ink": `color-mix(in oklab, ${deepColor} 80%, black)`,
		"--shop-surface":
			appearance.surfaceColor ??
			`color-mix(in oklab, ${primaryColor} 3%, #f4f6f9)`,
		"--shop-card": card ?? "#ffffff",
		"--shop-ink": `color-mix(in oklab, ${deepColor} 22%, #20242f)`,
		"--shop-muted": `color-mix(in oklab, ${deepColor} 12%, #6b7280)`,
		...chosen(appearance, card ? rgb(card) : WHITE),
		"--shop-hero": `linear-gradient(170deg, ${primaryColor} 0%, color-mix(in oklab, ${primaryColor} 55%, ${deepColor}) 58%, ${deepColor} 100%)`,
		...parts(appearance, primaryColor, onBrand, deepColor),
	};
}

function darkVars(appearance: ShopperAppearance): Vars {
	const { primaryColor, deepColor, accentColor, dealColor } = appearance;
	const primary = rgb(primaryColor);
	const page = appearance.surfaceColor ? rgb(appearance.surfaceColor) : null;
	const surface = page ?? mix([14, 16, 20], primary, 0.05);
	const card = appearance.cardColor
		? rgb(appearance.cardColor)
		: page
			? mix(page, WHITE, 0.07)
			: mix([24, 27, 33], primary, 0.04);
	// An ink brand would be a black button on a black page: it turns light instead.
	const brand =
		contrast(primary, surface) < 1.35 ? mix(primary, WHITE, 0.88) : primary;
	const onBrand = hex(onFill(brand));
	const brandText = readable(primary, card, 4.5, WHITE);
	return {
		"color-scheme": "dark",
		"--shop-brand": hex(brand),
		"--shop-on-brand": onBrand,
		"--shop-brand-text": hex(brandText),
		"--shop-deep": deepColor,
		"--shop-accent": accentColor,
		"--shop-deal": hex(readable(rgb(dealColor), card, 4.5, WHITE)),
		"--shop-brand-soft": hex(mix(card, brandText, 0.12)),
		"--shop-brand-line": hex(mix(card, brandText, 0.24)),
		// Accent chips stay light with dark text on them, like a badge.
		"--shop-accent-soft": `color-mix(in oklab, ${accentColor} 35%, white)`,
		"--shop-accent-ink": `color-mix(in oklab, ${deepColor} 80%, black)`,
		"--shop-surface": hex(surface),
		"--shop-card": hex(card),
		"--shop-ink": "#f2f4f7",
		"--shop-muted": "#9aa2af",
		...chosen(appearance, card),
		"--shop-hero": `linear-gradient(170deg, ${primaryColor} 0%, color-mix(in oklab, ${primaryColor} 55%, ${deepColor}) 58%, ${deepColor} 100%)`,
		...parts(appearance, hex(brand), onBrand, hex(brand)),
	};
}

/**
 * The kit pieces the chat borrows (dialogs, the drawer, outline buttons) read the admin's
 * tokens, which follow the admin's own light or dark setting. Here they follow the chat.
 */
const KIT: Vars = {
	"--background": "var(--shop-surface)",
	"--foreground": "var(--shop-ink)",
	"--card": "var(--shop-card)",
	"--card-foreground": "var(--shop-ink)",
	"--popover": "var(--shop-card)",
	"--popover-foreground": "var(--shop-ink)",
	"--muted": "var(--shop-brand-soft)",
	"--muted-foreground": "var(--shop-muted)",
	"--accent": "var(--shop-brand-soft)",
	"--accent-foreground": "var(--shop-ink)",
	"--border": "var(--shop-brand-line)",
	"--input": "var(--shop-brand-line)",
	"--primary": "var(--shop-brand)",
	"--primary-foreground": "var(--shop-on-brand)",
	"--ring": "var(--shop-brand-text)",
};

/** The kit's radius scale multiplies `--radius` (theme.css); 0.5rem is its own default. */
const RADIUS = { round: "0.5rem", soft: "0.3rem", square: "0.125rem" } as const;

/** Pill, a rounded rectangle or nearly square: every button reads `--shop-btn`. */
const BUTTONS = {
	pill: "9999px",
	rounded: "0.875rem",
	square: "0.25rem",
} as const;

// Geist is the app's own --font-sans (theme.css); the others override it for the chat.
const GEIST =
	'"Geist Variable", "IBM Plex Sans Arabic", ui-sans-serif, system-ui, sans-serif';
const FONTS: Record<string, string> = {
	manrope:
		'"Manrope Variable", "IBM Plex Sans Arabic", ui-sans-serif, system-ui, sans-serif',
	// The device's own typeface: the chat reads like the app around it.
	system: 'system-ui, -apple-system, "Segoe UI", Roboto, "IBM Plex Sans Arabic", sans-serif',
};

const block = (vars: Vars) =>
	Object.entries(vars)
		.map(([name, value]) => `${name}:${value}`)
		.join(";");

/** The hosted page's scope: the page is the chat. */
export const PAGE_SCOPE = ":root";

/**
 * The stylesheet of a chat in `appearance`, on what `scope` selects: `PAGE_SCOPE`, or an inline
 * chat's own element. `color-scheme` and `--radius` are set there with the colours.
 */
export function shopperThemeCss(
	appearance: ShopperAppearance,
	scope: string,
): string {
	// A typeface of the device comes first; the preset (or Geist) follows as its fallback.
	// Always written: a chat inside another page must not wear that page's typeface.
	const preset = FONTS[appearance.font] ?? GEIST;
	const font = appearance.deviceFont
		? `"${appearance.deviceFont}", ${preset}`
		: preset;
	const base: Vars = {
		"--radius": RADIUS[appearance.corners],
		"--shop-btn": BUTTONS[appearance.buttons],
		"--font-sans": font,
		"font-family": font,
		...KIT,
	};
	const light = `${scope}{${block({ ...base, ...lightVars(appearance) })}}`;
	const dark = `${scope}{${block({ ...base, ...darkVars(appearance) })}}`;
	// A page colour of its own decides the scheme: light text on a dark page, dark on a light.
	// Without one, a card colour does.
	const ground = appearance.surfaceColor ?? appearance.cardColor;
	if (ground) {
		return luminance(rgb(ground)) < 0.2 ? dark : light;
	}
	if (appearance.scheme === "light") {
		return light;
	}
	if (appearance.scheme === "dark") {
		return dark;
	}
	return `${light}@media (prefers-color-scheme:dark){${dark}}`;
}
